import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { unstable_batchedUpdates } from 'react-dom';
import * as Popover from '@skipper/ui';
import { useSimpleCalendar } from '@components/calendar/useCalendarHook';
import { Text } from '@skipper/ui';
import { capitalizeFirstLetter } from '@utils/capitalizeFirst';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';

dayjs.extend(utc);

export function CalendarV2({
    onDayChange,
    initialDate,
    predicate,
    children,
    holidays,
    showTime,
    onAddTime,
    onRemoveTime,
    autoClose = true,
    maxEndDate,
    useRound = false,
    useSmall = false,
    selectTeal = false,
}: {
    onDayChange?: (dayJs: dayjs.Dayjs) => void;
    initialDate?: string | dayjs.Dayjs;
    children: React.ReactNode;
    holidays?: { [key: string]: boolean };
    predicate?: (
        day: dayjs.Dayjs,
        setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    ) => Promise<boolean>;
    showTime?: boolean;
    onAddTime?: () => void;
    onRemoveTime?: () => void;
    autoClose?: boolean;
    maxEndDate?: dayjs.Dayjs;
    useRound?: boolean;
    useSmall?: boolean;
    selectTeal?: boolean;
}) {
    const calender = useSimpleCalendar({
        initialState: {
            currentDate: dayjs(initialDate).isValid() ? dayjs(initialDate) : dayjs(),
        },
        state: {
            selectedDate: dayjs(initialDate),
        },
        onStateChange: (state, actionName) => {
            const newState = state();
            onDayChange?.(newState?.selectedDate);
        },
        notfiyOnStateChanges: new Set(['selectedDate']),
    });
    const [open, setOpen] = useState(false);

    const callback = useCallback(
        async (newDay: dayjs.Dayjs) => {
            if (predicate) {
                const res = await predicate(newDay, setOpen);
                if (!res) return;
            }
            calender.selectDate(newDay);
            if (autoClose) {
                requestAnimationFrame(() => {
                    setOpen(false);
                });
            }
        },
        [calender.selectDate, predicate, setOpen, autoClose],
    );

    return (
        <Popover.Popover
            css={{ minWidth: '500px' }}
            open={open}
            onOpenChange={(val) => {
                requestAnimationFrame(() => {
                    setOpen(val);
                });
            }}>
            <Popover.PopoverTrigger
                aria-label={
                    !calender?.getState?.()?.selectedDay
                        ? 'Choose Date'
                        : calender?.getState?.()?.selectedDay?.('MMMM DD, YYYY')
                }>
                {children}
            </Popover.PopoverTrigger>
            <Popover.PopoverContent>
                <Popover.CalendarContainer className={useSmall ? '!w-[50%] !min-w-[300px]' : ''}>
                    <CalendarHeader
                        getPrevMonth={calender.getPreviousMonth}
                        getNextMonth={calender.getNextMonth}
                        currentDate={calender?.getState?.()?.currentDate?.format('MMMM YYYY')}
                    />
                    <CalendarDaysOfWeek calendar={calender} />
                    {calender.getRows().map((cell: any, index: number) => {
                        return useRound ? (
                            <RoundDateCellWithDot
                                key={index}
                                cell={cell}
                                hasEvent={false}
                                index={index}
                                selectTeal={selectTeal}
                                selectedDay={dayjs(initialDate)}
                                selectedByUser={dayjs(initialDate)}
                                disableDate={
                                    !!maxEndDate
                                        ? dayjs(cell?.getValue?.().date).isAfter(maxEndDate)
                                        : false
                                }
                                holidays={[]}
                                setDay={() => {
                                    const values = cell?.getValue?.();
                                    const date = values?.date;

                                    callback(
                                        date
                                            ?.set(
                                                'hour',
                                                initialDate && dayjs(initialDate)?.isValid()
                                                    ? dayjs(initialDate).hour()
                                                    : 23,
                                            )
                                            .set(
                                                'minutes',
                                                initialDate && dayjs(initialDate)?.isValid()
                                                    ? dayjs(initialDate).minute()
                                                    : 59,
                                            ),
                                    );
                                }}
                            />
                        ) : (
                            <DateCell
                                key={index}
                                cell={cell}
                                index={index}
                                selectedDay={dayjs(initialDate)}
                                holidays={holidays}
                                disableDate={
                                    !!maxEndDate
                                        ? dayjs(cell?.getValue?.().date).isAfter(maxEndDate)
                                        : false
                                }
                                setDay={() => {
                                    const values = cell?.getValue?.();
                                    const date = values?.date;

                                    callback(
                                        date
                                            ?.set(
                                                'hour',
                                                initialDate && dayjs(initialDate)?.isValid()
                                                    ? dayjs(initialDate).hour()
                                                    : 23,
                                            )
                                            .set(
                                                'minutes',
                                                initialDate && dayjs(initialDate)?.isValid()
                                                    ? dayjs(initialDate).minute()
                                                    : 59,
                                            ),
                                    );
                                }}
                            />
                        );
                    })}
                    <Popover.Cols
                        colSize="col7"
                        css={{
                            pt: '$4',
                            pb: '$2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'start',
                        }}>
                        {showTime ? (
                            <>
                                <TimeSelector
                                    minute={
                                        dayjs(initialDate)?.isValid()
                                            ? dayjs(initialDate).minute()
                                            : 59
                                    }
                                    hour={
                                        dayjs(initialDate)?.isValid()
                                            ? dayjs(initialDate).hour()
                                            : 23
                                    }
                                    setDate={(hours, minutes) => {
                                        callback(
                                            dayjs(initialDate)?.isValid()
                                                ? dayjs(initialDate)
                                                      ?.set('hour', hours)
                                                      ?.set('minutes', minutes)
                                                : dayjs()
                                                      ?.set('hour', hours)
                                                      ?.set('minutes', minutes),
                                        );
                                    }}
                                />
                                {onRemoveTime && (
                                    <button onClick={onRemoveTime} className={'ml-4'}>
                                        Remove time
                                    </button>
                                )}
                            </>
                        ) : (
                            onAddTime && <button onClick={onAddTime}>Add time</button>
                        )}
                    </Popover.Cols>
                </Popover.CalendarContainer>
            </Popover.PopoverContent>
        </Popover.Popover>
    );
}

export const RoundCalendar = ({
    setSelectedMonth,
    setSelectedYear,
    setSelectedDay,
    daysWithEvents,
    initialDate,
    selectedDay,
    holidays,
    selectedMonth,
    showLoadingState = false,
    disableControls = false,
}: {
    daysWithEvents: string[];
    initialDate?: dayjs.Dayjs;
    selectedDay?: dayjs.Dayjs;
    selectedMonth?: dayjs.Dayjs;
    holidays?: { [key: string]: boolean };
    setSelectedMonth: any;
    setSelectedDay: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>;
    setSelectedYear?: any;
    showLoadingState?: boolean;
    disableControls?: boolean;
}) => {
    const calendar = useSimpleCalendar({
        initialState: {
            currentDate: selectedDay,
        },
        state: {
            selectedDate: selectedDay,
        },
        onStateChange: (state, actionName) => {
            const newState = state();

            if (!!newState?.selectedDate) setSelectedDay(newState?.selectedDate);
            if (!!newState?.selectedDate && newState?.selectedDate?.month() !== selectedMonth) {
                setSelectedMonth(newState?.selectedDate?.month());
                setSelectedYear(newState?.selectedDate?.year());
            }
            if (!!newState?.currentDate) {
                setSelectedYear(newState?.currentDate?.year());
                setSelectedMonth(newState?.currentDate?.month());
            }
        },
        notfiyOnStateChanges: new Set(['selectedDate', 'currentDate']),
    });

    useEffect(() => {
        calendar.selectDate(selectedDay);
    }, [selectedDay, calendar]);

    const callback = useCallback(
        async (newDay: dayjs.Dayjs) => {
            calendar.selectDate(newDay);
        },
        [calendar],
    );

    useEffect(() => {
        calendar.setState((s) => {
            return {
                ...s,
                currentDate: dayjs(selectedDay).startOf('month'),
            };
        }, 'selectedDate');
    }, [selectedDay, calendar]);

    return (
        <Popover.CalendarContainerSmall className="!w-[50%]">
            <CalendarHeader
                getPrevMonth={calendar.getPreviousMonth}
                getNextMonth={calendar.getNextMonth}
                currentDate={calendar?.getState?.()?.currentDate?.format('MMMM YYYY')}
                showLoadingState={showLoadingState}
                disableControls={disableControls}
            />
            <CalendarDaysOfWeek calendar={calendar} />
            {calendar.getRows().map((cell: any, index: number) => {
                return (
                    <RoundDateCellWithDot
                        key={index}
                        cell={cell}
                        hasEvent={daysWithEvents.includes(
                            dayjs(cell?.getValue?.().date).format('YYYY-MM-DD'),
                        )}
                        index={index}
                        selectedDay={dayjs(initialDate)}
                        selectedByUser={selectedDay}
                        holidays={[]}
                        setDay={() => {
                            const values = cell?.getValue?.();
                            const date = values?.date;

                            callback(
                                date
                                    ?.set(
                                        'hour',
                                        initialDate && dayjs(initialDate)?.isValid()
                                            ? dayjs(initialDate).hour()
                                            : 23,
                                    )
                                    .set(
                                        'minutes',
                                        initialDate && dayjs(initialDate)?.isValid()
                                            ? dayjs(initialDate).minute()
                                            : 59,
                                    ),
                            );
                        }}
                    />
                );
            })}
        </Popover.CalendarContainerSmall>
    );
};

// TODO use RoundCalendar - need to update motnh and year values (not sure if not error prone)
export const CoursegridRoundCalendar = ({
    setSelectedMonth,
    // setSelectedYear,
    setSelectedDay,
    daysWithEvents,
    initialDate,
    selectedDay,
    holidays,
    selectedMonth,
    showLoadingState = false,
    disableControls = false,
}: {
    daysWithEvents: string[];
    initialDate?: dayjs.Dayjs;
    selectedDay?: dayjs.Dayjs;
    selectedMonth?: dayjs.Dayjs;
    holidays?: { [key: string]: boolean };
    setSelectedMonth: any;
    setSelectedDay: React.Dispatch<React.SetStateAction<dayjs.Dayjs>>;
    // setSelectedYear?: any;
    showLoadingState?: boolean;
    disableControls?: boolean;
}) => {
    const calendar = useSimpleCalendar({
        initialState: {
            currentDate: selectedDay,
        },
        state: {
            selectedDate: selectedDay,
        },
        onStateChange: (state, actionName) => {
            const newState = state();

            if (!!newState?.selectedDate) setSelectedDay(newState?.selectedDate);
            if (!!newState?.selectedDate && newState?.selectedDate?.month() !== selectedMonth)
                setSelectedMonth(dayjs().month(newState?.selectedDate?.month()));
            if (!!newState?.currentDate)
                setSelectedMonth(dayjs().month(newState?.currentDate?.month()));
        },
        notfiyOnStateChanges: new Set(['selectedDate', 'currentDate']),
    });

    useEffect(() => {
        calendar.selectDate(selectedDay);
    }, [selectedDay, calendar]);

    const callback = useCallback(
        async (newDay: dayjs.Dayjs) => {
            calendar.selectDate(newDay);
        },
        [calendar],
    );

    useEffect(() => {
        calendar.setState((s) => {
            return {
                ...s,
                currentDate: dayjs(selectedDay).startOf('month'),
            };
        }, 'selectedDate');
    }, [selectedDay, calendar]);

    return (
        <Popover.CalendarContainerSmall className="!w-[50%]">
            <CalendarHeader
                getPrevMonth={calendar.getPreviousMonth}
                getNextMonth={calendar.getNextMonth}
                currentDate={calendar?.getState?.()?.currentDate?.format('MMMM YYYY')}
                showLoadingState={showLoadingState}
                disableControls={disableControls}
            />
            <CalendarDaysOfWeek calendar={calendar} />
            {calendar.getRows().map((cell: any, index: number) => {
                return (
                    <RoundDateCellWithDot
                        key={index}
                        cell={cell}
                        hasEvent={daysWithEvents.includes(
                            dayjs(cell?.getValue?.().date).format('YYYY-MM-DD'),
                        )}
                        index={index}
                        selectedDay={dayjs(initialDate)}
                        selectedByUser={selectedDay}
                        holidays={[]}
                        setDay={() => {
                            const values = cell?.getValue?.();
                            const date = values?.date;

                            callback(
                                date
                                    ?.set(
                                        'hour',
                                        initialDate && dayjs(initialDate)?.isValid()
                                            ? dayjs(initialDate).hour()
                                            : 23,
                                    )
                                    .set(
                                        'minutes',
                                        initialDate && dayjs(initialDate)?.isValid()
                                            ? dayjs(initialDate).minute()
                                            : 59,
                                    ),
                            );
                        }}
                    />
                );
            })}
        </Popover.CalendarContainerSmall>
    );
};

export const CalendarHeader = ({
    getPrevMonth,
    getNextMonth,
    currentDate,
    showLoadingState,
    disableControls = false,
}: {
    getPrevMonth: any;
    getNextMonth: any;
    currentDate: string;
    showLoadingState?: boolean;
    disableControls?: boolean;
}) => {
    const iconClassName = disableControls
        ? 'text-gray-300 cursor-not-allowed'
        : 'text-gray-700 cursor-pointer';
    return (
        <Popover.Cols colSize="col7">
            <Popover.Grid>
                <Popover.Cols>
                    <button
                        type="button"
                        aria-label="Previous month"
                        onClick={getPrevMonth}
                        disabled={disableControls}
                        className="flex items-center justify-start focus:outline-none  rounded-md">
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" className={iconClassName} />
                    </button>
                </Popover.Cols>
                <Popover.Cols colSize="col5">
                    <div className="w-full flex justify-start items-center">
                        <Text
                            size="large"
                            as="h2"
                            aria-live="polite"
                            aria-label={currentDate}
                            className="text-lg leading-6 font-bold mx-auto">
                            {currentDate}
                        </Text>
                        {showLoadingState && (
                            <div className="w-5 h-5 ml-2">
                                <SimpleSpinner
                                    isSmall
                                    className="fill-gray-500 w-5 h-5"
                                    noPaddings
                                />
                            </div>
                        )}
                    </div>
                </Popover.Cols>
                <Popover.Cols>
                    <button
                        type="button"
                        aria-label="Next month"
                        onClick={getNextMonth}
                        disabled={disableControls}
                        className="flex items-center justify-end focus:outline-none rounded-md">
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            size="lg"
                            className={iconClassName + ' ml-5'}
                        />
                    </button>
                </Popover.Cols>
            </Popover.Grid>
        </Popover.Cols>
    );
};

export const CalendarDaysOfWeek = ({ calendar }: { calendar: any }) => {
    return (
        <Popover.Cols colSize="col7">
            <Popover.Grid>
                {calendar.getHeaders().map((day: string, idx: number) => {
                    return (
                        <Popover.Cols
                            key={day}
                            css={{
                                display: 'flex',
                                justifyItems: 'center',
                                alignItems: 'center',
                                width: '100%',
                                textAlign: 'center',
                                py: '$1',
                                marginTop: '$3',
                            }}
                            className="pointer-events-none w-full text-center">
                            <Text css={{ textAlign: 'center' }}>{capitalizeFirstLetter(day)}</Text>
                        </Popover.Cols>
                    );
                })}
            </Popover.Grid>
        </Popover.Cols>
    );
};

function DateCell({
    cell,
    index,
    selectedDay,
    holidays,
    setDay,
    disableDate = false,
}: {
    cell: any;
    index: number;
    selectedDay?: dayjs.Dayjs;
    setDay: () => void;
    holidays?: { [key: string]: boolean };
    disableDate?: boolean;
}) {
    const values = cell?.getValue?.();
    const vals = useCellAttributes({ cell: values });

    if (!values?.date) return <div className="col-span-1" />;
    const isHoliday = holidays?.[values?.date?.format('DD/MM/YYYY')];

    return (
        <Popover.CalendarColumn
            data-current-day={values?.isToday}
            data-current-isholiday={isHoliday}
            data-current-selected={selectedDay?.isSame(values?.date, 'day')}
            disabled={isHoliday}
            disableDate={disableDate}
            onClick={setDay}
            key={index}
            firstIndex={index === 0}
            topLeft={vals.topLeft}
            topRight={vals.topRight}
            topBorder={vals.topBorder}
            rightBorder={vals.rightBorder}
            leftBorder={vals.leftBorder}
            bottomRight={vals.bottomRight}
            bottomSecondToLastRow={vals.bottomSecondToLastRow}
            bottomRow={vals.bottomRow}
            bottomLeft={vals.bottomLeft}
            bottomSecondLast={vals.bottomSecondLast}
            firstItem={vals.firstItem}
            emptyCell={vals.emptyCell}>
            {values?.dayOfMonth}
        </Popover.CalendarColumn>
    );
}

export function RoundDateCellWithDot({
    cell,
    index,
    selectedDay,
    holidays,
    setDay,
    hasEvent,
    selectedByUser,
    disableDate = false,
    selectTeal = false,
}: {
    cell: any;
    index: number;
    selectedDay?: dayjs.Dayjs;
    setDay: () => void;
    holidays?: { [key: string]: boolean };
    hasEvent?: boolean;
    selectedByUser?: dayjs.Dayjs;
    disableDate?: boolean;
    selectTeal?: boolean;
}) {
    const values = cell?.getValue?.();
    const vals = useCellAttributes({ cell: values });

    if (!values?.date) return <div className="col-span-1" />;
    const isHoliday = holidays?.[values?.date?.format('DD/MM/YYYY')];

    return (
        <Popover.BorderlessCalendarColumn
            data-current-day={values?.isToday}
            data-current-isholiday={isHoliday}
            data-current-selected={selectedDay?.isSame(values?.date, 'day')}
            data-user-selected={selectedByUser?.isSame(values?.date, 'day') && !values?.isToday}
            data-user-selected-teal={
                selectedByUser?.isSame(values?.date, 'day') && !values?.isToday && selectTeal
            }
            disabled={isHoliday}
            disableDate={disableDate}
            onClick={setDay}
            key={index}
            firstIndex={index === 0}
            firstItem={vals.firstItem}
            emptyCell={vals.emptyCell}>
            <div className="flex flex-col items-center mt-1">
                <div>{values?.dayOfMonth}</div>
                {
                    <div
                        className={`h-[4px] w-[4px] mx-auto rounded-full ${
                            hasEvent ? 'bg-teal-600' : 'bg-transparent'
                        }`}
                    />
                }
            </div>
        </Popover.BorderlessCalendarColumn>
    );
}

function useCellAttributes({ cell }: { cell: any }) {
    const state = useMemo(
        () => ({
            topLeft:
                (cell?.firstWeek && cell?.startOfWeek) || (cell?.startOfWeek && cell?.roundStart),
            topRight: cell?.firstWeek && cell?.endOfWeek,
            bottomLeft: cell?.lastWeek && cell?.startOfWeek,
            bottomRight: cell?.lastDayOfMonth,
            bottomSecondToLastRow:
                cell?.secondToLast && cell?.lastWeek && cell?.endOfWeek && !cell.lastDayOfMonth,
            topBorder: !!cell?.date,
            rightBorder: !!cell?.date,
            leftBorder: cell?.startOfWeek && !cell?.firstWeek,
            bottomRow: cell?.lastWeek,
            bottomSecondLast: cell?.lastWeek && cell?.secondToLast,
            firstItem: cell?.firstWeek && cell?.startOfWeek,
            emptyCell: !cell?.date,
        }),
        [cell],
    );

    return state;
}

function TimeSelector({
    hour,
    minute,
    setDate,
}: {
    hour: number;
    minute: number;
    setDate: (hours: number, minutes: number) => void;
}) {
    const [h, setH] = useState(hour > 12 ? hour - 12 : hour);
    const [m, setM] = useState(minute);
    const [isAm, setIsAm] = useState(() => hour <= 12);

    const handleInput = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const value = Number(ev.target.value);
            if (value <= 12 && value >= 0) {
                setH(value);
            }
        },
        [setH],
    );

    const handleBlur = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const value = h;
            if (value <= 12 && value >= 0) {
                setDate(!isAm ? value + 12 : value, m);
            }
        },
        [h, isAm, m, setDate],
    );

    const handleInputMinutes = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const value = Number(ev.target.value);
            if (value <= 59 && value >= 0) {
                setM(value);
            }
        },
        [setM],
    );

    const handleBlurMinutes = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const value = m;
            if (m <= 59 && m >= 0) {
                setDate(h, value);
            }
        },
        [m, h, isAm, setDate],
    );

    const hourFmt = h?.toString().length === 1 ? `0${h}` : `${h}`;
    const minuteFmt = m?.toString().length === 1 ? `0${m}` : `${m}`;
    return (
        <div className="flex gap-x-1">
            <input
                type="number"
                className="form-input border border-gray-300 w-12 rounded-sm focus:outline-none focus:ring-0 text-center focus:border-teal-600"
                value={hourFmt}
                onChange={handleInput}
                onBlur={handleBlur}
            />
            <div className="text-2xl flex items-center justify-center">:</div>
            <input
                type="number"
                value={minuteFmt}
                className="form-input border border-gray-300 w-12 rounded-sm focus:outline-none focus:ring-0 text-center focus:border-teal-600"
                onChange={handleInputMinutes}
                onBlur={handleBlurMinutes}
            />
            <div className="divide-y flex overflow-hidden">
                <button
                    type="button"
                    onClick={() => {
                        unstable_batchedUpdates(() => {
                            setDate(h > 12 ? h - 12 : h, m);
                            setIsAm(true);
                        });
                    }}
                    className={`px-2 text-sm border-l border-t border-b rounded-tl-md rounded-bl-md ${
                        isAm
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-gray-100 border-gray-300'
                    }`}>
                    AM
                </button>
                <button
                    type="button"
                    onClick={() => {
                        unstable_batchedUpdates(() => {
                            setDate(h <= 12 ? h + 12 : h, m);
                            setIsAm(false);
                        });
                    }}
                    className={`px-2 text-sm border-r border-t border-b rounded-tr-md rounded-br-md ${
                        !isAm
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-gray-100 border-gray-300'
                    }`}>
                    PM
                </button>
            </div>
        </div>
    );
}
